import axios, {
  AxiosError,
  AxiosResponse,
  InternalAxiosRequestConfig,
} from "axios";
import { jwtDecode } from "jwt-decode";
import posthog from "posthog-js";
import { ReactText } from "react";
import { toast } from "react-toastify";
import { ToastContent } from "../../components/ToastContent/ToastContent";
import {
  B2C_LOCAL_STORAGE_EMAIL,
  BASE_URL,
  BASE_WORKFLOW_URL_V2,
  BASE_WORKFLOW_URL_V3,
  BASE_WORKFLOW_URL_V4,
  IS_DEMO,
  LOGOUT_USER,
  REFRESH_TOKEN,
} from "../../config/constants";
import { normalizeUrl } from "./urlMatchers";
import { getDomainHintFromEmail, MSAL_CONFIG } from "../../utils/auth";

interface CustomAxiosRequestConfig extends InternalAxiosRequestConfig {
  _retry: boolean;
  metadata?: { startTime: number };
}

interface ErrorResponseData {
  errorDescription?: string;
  errorCode?: number;
  errorName?: string;
}

interface CustomAxiosError extends AxiosError<ErrorResponseData> {
  config: CustomAxiosRequestConfig;
}

export const workflowInstance_V2 = axios.create({
  baseURL: BASE_WORKFLOW_URL_V2,
});

export const workflowInstance_V3 = axios.create({
  baseURL: BASE_WORKFLOW_URL_V3,
});

export const workflowInstance_V4 = axios.create({
  baseURL: BASE_WORKFLOW_URL_V4,
});

let toastId: ReactText;

const ERROR_MESSAGES = {
  DUPLICATE_CTO_APPROVAL:
    "The CTO Closure Approval can't be approved twice by the same person or by a person with the same position as an already approved user.",
  NETWORK_ERROR:
    "It looks like your internet connection was interrupted. Please check your network and try again shortly.",
  UNAUTHORIZED_OR_FORBIDDEN:
    "Your session has expired or you don't have sufficient permissions, click here to sign in again.",
  GENERIC_ERROR: "Error, click here to reload the page.",
};

const ERROR_NAMES = {
  FORBIDDEN: "Forbidden",
};

/**
 * Refreshes the access token through Reef's MSAL wrapper.
 *
 * The stored email is read on every call (not at module load) so a session
 * started after the app booted, without a page reload, can still refresh.
 * Rejects when the refresh fails so callers never hang on a pending promise.
 */
export const getToken = async (): Promise<string> => {
  const email = localStorage.getItem(B2C_LOCAL_STORAGE_EMAIL);

  if (!email) {
    window.location.replace("/login");
    throw new Error("No stored user email, redirecting to login");
  }

  const { mslaGetToken } = await import("../../utils/msal");

  return new Promise<string>((resolve, reject) => {
    void mslaGetToken(
      MSAL_CONFIG(email, getDomainHintFromEmail(email)),
      resolve,
      (reason) => reject(new Error(`Token refresh failed (${reason})`)),
    );
  });
};

const logoutUser = () => {
  const event = new CustomEvent(LOGOUT_USER);
  window.dispatchEvent(event);
};

const getToastMessage = (error: CustomAxiosError) => {
  if (error.message === "Network Error") return ERROR_MESSAGES.NETWORK_ERROR;

  if ([401, 403].includes(error.response?.status ?? 0)) {
    if (error.response?.data?.errorName === ERROR_NAMES.FORBIDDEN) {
      return error.response?.data?.errorDescription;
    }
    return ERROR_MESSAGES.UNAUTHORIZED_OR_FORBIDDEN;
  }

  if ([400, 409].includes(error.response?.status ?? 0) && error.response?.data?.errorDescription) {
    return error.response.data.errorDescription;
  }

  return ERROR_MESSAGES.GENERIC_ERROR;
};

export const interceptors = {
  request: async (config: InternalAxiosRequestConfig) => {
    (config as CustomAxiosRequestConfig).metadata = { startTime: Date.now() };
    const PLAYWRIGHT_TOKEN = localStorage.getItem("jwtToken");
    const token = axios.defaults.headers.common.Authorization as string;
    const bearerToken = token?.replace("Bearer ", "");
    config.headers.auth = bearerToken;
    config.headers.Authorization = token;

    const decoded =
      token && !IS_DEMO && !PLAYWRIGHT_TOKEN
        ? jwtDecode(token.replace("Bearer ", ""))
        : undefined;
    if (decoded?.exp) {
      const expiration = new Date(decoded.exp * 1000);
      const dateNowPlus10 = new Date(Date.now() + 10 * 60 * 1000);
      const invalidated = expiration < dateNowPlus10;

      if (invalidated) {
        try {
          const newToken = await getToken();
          const event = new CustomEvent(REFRESH_TOKEN, { detail: newToken });
          window.dispatchEvent(event);
        } catch (refreshError) {
          // Keep the current token: it may still be valid for a few minutes,
          // and a 401 is retried once by the response interceptor anyway.
          console.error("Error refreshing token before request:", refreshError);
        }
      }
    }
    return config;
  },
  response: {
    success: (response: AxiosResponse) => {
      handleDates(response.data);
      const duration = (response.config as CustomAxiosRequestConfig).metadata?.startTime ?
        Date.now() - ((response.config as CustomAxiosRequestConfig).metadata?.startTime ?? Date.now()) : 0;
      posthog.capture('HTTP_REQUEST', {
        status_code: response.status,
        duration_ms: duration,
        url: normalizeUrl(response.config.url),
        method: response.config.method,
        browser_url: window.location?.href ?? null,
      });
      return response;
    },
    error: async (error: CustomAxiosError) => {
      const originalRequest = error.config;
      const duration = originalRequest?.metadata?.startTime ? Date.now() - originalRequest.metadata.startTime : 0;

      const isNetworkError = axios.isAxiosError(error) && error.code === 'ERR_NETWORK' && !error.response;
      posthog?.capture('HTTP_REQUEST', {
        status_code: isNetworkError ? 0 : error.response?.status ?? null,
        duration_ms: duration,
        url: normalizeUrl(originalRequest?.url),
        method: originalRequest?.method ?? null,
        browser_url: window.location.href,
        statusText: error.response?.statusText ?? null,
      });

      if (error.response?.status === 401 && !originalRequest?._retry) {
        originalRequest._retry = true;
        try {
          const webToken = await getToken();
          const bearerWebToken = `Bearer ${webToken}`;
          originalRequest.headers.auth = webToken;
          originalRequest.headers["Authorization"] = bearerWebToken;
          const event = new CustomEvent(REFRESH_TOKEN, { detail: webToken });
          window.dispatchEvent(event);
          return axios(originalRequest);
        } catch (refreshError) {
          // Fall through to the session-expired toast below.
          console.error("Error refreshing token after 401:", refreshError);
        }
      }
      // Show toast
      const message = getToastMessage(error);
      const xRequestId = error.response?.headers["x-request-id"];
      const idMessage = xRequestId ? `Id: ${xRequestId}` : undefined;

      if (!toast.isActive(toastId) && !IS_DEMO) {
        toastId = toast.error(ToastContent(message ?? "", idMessage), {
          onClick: () =>
            [401, 403].includes(error.response?.status ?? 0) &&
            error.response?.data?.errorName !== ERROR_NAMES.FORBIDDEN
              ? logoutUser()
              : window.location.reload(),
          autoClose: false,
          closeButton: true,
          draggable: false,
        });
      }

      const properties = {
        message: error.message || "Unknown error",
        name: error.name || "AxiosError",
        stack: error.stack || "No stack trace",
        code: error.code ?? null,
        status: error.response?.status ?? null,
        statusText: error.response?.statusText ?? null,
        url: error.config?.url || "Unknown URL",
        method: error.config?.method || "Unknown Method",
        app_version: __APP_VERSION__,
        x_request_id: xRequestId ?? null,
        error_category: "API_ERROR",
      };
      posthog.captureException(error, properties);

      if (error.response) {
        throw error.response.data;
      }
      throw error.message;
    },
  },
};

export const initApi = () => {
  // Default instance config
  axios.defaults.baseURL = BASE_URL;

  // Handle request auth
  axios.interceptors.request.use(interceptors.request);
  workflowInstance_V2.interceptors.request.use(interceptors.request);
  workflowInstance_V3.interceptors.request.use(interceptors.request);
  workflowInstance_V4.interceptors.request.use(interceptors.request);

  // Handle response errors
  axios.interceptors.response.use(
    interceptors.response.success,
    interceptors.response.error,
  );

  workflowInstance_V2.interceptors.response.use(
    interceptors.response.success,
    interceptors.response.error,
  );
  workflowInstance_V3.interceptors.response.use(
    interceptors.response.success,
    interceptors.response.error,
  );
  workflowInstance_V4.interceptors.response.use(
    interceptors.response.success,
    interceptors.response.error,
  );
};

const ISODateFormat =
  /^\d{4}-\d{2}-\d{2}T\d{2}:\d{2}:\d{2}(?:\.\d*)?(?:[-+]\d{2}:?\d{2}|Z)?$/;

const isIsoDateString = (value: unknown): value is string => {
  return typeof value === "string" && ISODateFormat.test(value);
};

const handleDates = (data: unknown) => {
  if (isIsoDateString(data)) return new Date(data);
  if (data === null || data === undefined || typeof data !== "object")
    return data;

  for (const [key, val] of Object.entries(data)) {
    if (isIsoDateString(val)) {
      // @ts-expect-error this is a hack to make the type checker happy
      data[key] = new Date(val);
    } else if (typeof val === "object") handleDates(val);
  }

  return data;
};
